import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { updateSnapshotDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { Snapshot } from "@custom-types/base-types";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { AccessLevelMenuButton } from "@components/access-level/access-level-menu-button";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useChangeAccessLevelBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-change-access-level-helper";

/** Show change snapshots access level button and dispatch action where the bulk dialog will act accordingly */
export function BulkChangeSnapshotsAccessLevel(): JSX.Element {
  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [newAccessLevel, setNewAccessLevel] =
    useState<FaroCoreApiTypes.ProjectAccessLevel>("private");

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(
    selectedEntitiesSelector("snapshots")
  );

  const { bulkDialogContent, successDialogContent } =
    useChangeAccessLevelBulkDialogContent("snapshot", newAccessLevel);

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onAccessLevelChange(
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ): void {
    dispatch(setBulkActionName("changeSnapshotAccessLevel"));
    setNewAccessLevel(accessLevel);

    for (const entity of selectedEntities) {
      const hasUserValidRole = isUserSnapshotAdmin(entity);

      if (!hasUserValidRole) {
        dispatch(
          updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message:
                "You are not allowed to change the access level of this snapshot",
            },
          })
        );
      }
    }

    setShouldShowDialog(true);
  }

  return (
    <>
      <AccessLevelMenuButton
        type="snapshot"
        onAccessLevelChange={onAccessLevelChange}
      />

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeSnapshotAccessLevel" &&
        shouldShowSuccessDialog && (
          <BulkSuccessDialog dialogContent={successDialogContent} />
        )}

      {bulkActionName === "changeSnapshotAccessLevel" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeSnapshotsAccessLevel}
          bulkActionCallback={(snapshot: Snapshot) =>
            dispatch(
              updateSnapshotDetails({
                snapshotId: snapshot.id,
                payload: { accessLevel: newAccessLevel },
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="snapshots"
        />
      )}
    </>
  );
}
